// inactivity.service.ts
import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { SessionTimeoutDialogComponent } from './session-timeout-dialog/session-timeout-dialog.component';
import { AuthService } from './auth.service';
import { SessionService } from '../session.service';

@Injectable({
  providedIn: 'root'
})
export class InactivityService {
  static instanceCount = 0;
  timeoutId: any;
  timeout = 1000 * 60 * 8; // 8 minutes of inactivity before the warning timer starts
  countdownDuration = 1000 * 60 * 5;
  isWatching = false;
  private readonly lastActivityStorageKey = 'complydome:lastactivityat';
  private readonly activityThrottleMs = 1000;
  private readonly activityEvents = [
    'click',
    'keydown',
    'mousedown',
    'mousemove',
    'pointerdown',
    'scroll',
    'touchmove',
    'touchstart',
    'wheel',
  ];
  private warningAt: number | null = null;
  private sessionExpiresAt: number | null = null;
  private sessionTimeoutDialogRef: MatDialogRef<SessionTimeoutDialogComponent> | null = null;
  private isLoggingOut = false;
  private lastRecordedActivityAt = 0;
  private readonly visibilityChangeHandler = () => {
    if (document.visibilityState === 'visible') {
      this.syncSessionState();
    }
  };
  private readonly storageChangeHandler = (event: StorageEvent) => {
    if (event.key === this.lastActivityStorageKey) {
      this.syncSessionState();
    }
  };

  constructor(
    private dialog: MatDialog,
    private authService: AuthService,
    private sessionService: SessionService,
  ) {

    this.resetTimer = this.resetTimer.bind(this);
    InactivityService.instanceCount++;
    console.log(`InactivityService instance count: ${InactivityService.instanceCount}`);
  }

  startWatching() {
    if (!this.isWatching) {
      console.log(`${new Date().toISOString()} - Starting to watch for inactivity`);
      this.isWatching = true;
      this.resetTimer();
      this.activityEvents.forEach((eventName) => {
        document.addEventListener(eventName, this.resetTimer, true);
      });
      document.addEventListener('visibilitychange', this.visibilityChangeHandler, true);
      window.addEventListener('storage', this.storageChangeHandler);
    }
  }

  resetTimer() {
    if (!this.isWatching || this.sessionTimeoutDialogRef) {
      return;
    }

    const activityAt = this.recordActivity();
    this.scheduleSessionStateChecks(activityAt);
  }

  extendSession(): void {
    if (!this.isWatching) {
      return;
    }

    this.sessionTimeoutDialogRef?.close();
    const activityAt = this.recordActivity(true);
    this.scheduleSessionStateChecks(activityAt);
  }

  syncSessionState(): 'inactive' | 'active' | 'warning-pending' | 'warning-visible' | 'expired' {
    if (!this.isWatching || this.warningAt === null || this.sessionExpiresAt === null) {
      return 'inactive';
    }

    const now = Date.now();
    const lastActivityAt = this.getLastActivityAt(now);
    this.warningAt = lastActivityAt + this.timeout;
    this.sessionExpiresAt = this.warningAt + this.countdownDuration;

    if (now >= this.sessionExpiresAt) {
      this.logoutDueToInactivity();
      return 'expired';
    }

    if (now >= this.warningAt) {
      this.scheduleNextCheck(this.sessionExpiresAt - now);

      if (document.visibilityState === 'visible') {
        this.showDialog();
        return 'warning-visible';
      }

      return 'warning-pending';
    }

    this.closeStaleWarningDialog();
    this.scheduleNextCheck(this.warningAt - now);
    return 'active';
  }

  logoutDueToInactivity(): void {
    if (this.isLoggingOut) {
      return;
    }

    this.isLoggingOut = true;
    this.stopWatching();
    this.sessionService.clear();
    this.authService.logout();
    location.href = '/login';
  }

  private showDialog() {
    const sessionTimeoutDialogOpen = this.dialog.openDialogs.some(
      ({ componentInstance }) => componentInstance instanceof SessionTimeoutDialogComponent
    );

    if (!this.isWatching || sessionTimeoutDialogOpen || this.sessionExpiresAt === null) {
      return;
    }

    this.sessionTimeoutDialogRef = this.dialog.open(SessionTimeoutDialogComponent, {
      width: '300px',
      disableClose: true,
      data: {
        sessionExpiresAt: this.sessionExpiresAt,
      },
    });

    this.sessionTimeoutDialogRef.afterClosed().subscribe(() => {
      this.sessionTimeoutDialogRef = null;
    });
  }

  private scheduleSessionStateChecks(referenceTime = Date.now()): void {
    this.warningAt = referenceTime + this.timeout;
    this.sessionExpiresAt = this.warningAt + this.countdownDuration;
    this.scheduleNextCheck(this.timeout);
  }

  private recordActivity(force = false): number {
    const now = Date.now();
    if (!force && now - this.lastRecordedActivityAt < this.activityThrottleMs) {
      return this.lastRecordedActivityAt;
    }

    this.lastRecordedActivityAt = now;
    this.sessionService.setValue('last_activity_at', now);

    return now;
  }

  private getLastActivityAt(fallback = Date.now()): number {
    const storedLastActivityAt = Number(this.sessionService.getValue('last_activity_at'));

    return Number.isFinite(storedLastActivityAt) && storedLastActivityAt > 0
      ? storedLastActivityAt
      : fallback;
  }

  private closeStaleWarningDialog(): void {
    if (!this.sessionTimeoutDialogRef) {
      return;
    }

    this.sessionTimeoutDialogRef.close();
    this.sessionTimeoutDialogRef = null;
  }

  private scheduleNextCheck(delayMs: number): void {
    clearTimeout(this.timeoutId);

    if (!this.isWatching) {
      return;
    }

    this.timeoutId = setTimeout(() => this.syncSessionState(), Math.max(delayMs, 0));
  }

  stopWatching() {
    if (this.isWatching) {
      console.log(`${new Date().toISOString()} - Stopping inactivity monitoring`);
      this.isWatching = false;
      clearTimeout(this.timeoutId);
      this.warningAt = null;
      this.sessionExpiresAt = null;
      this.sessionTimeoutDialogRef?.close();
      this.sessionTimeoutDialogRef = null;
      this.activityEvents.forEach((eventName) => {
        document.removeEventListener(eventName, this.resetTimer, true);
      });
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler, true);
      window.removeEventListener('storage', this.storageChangeHandler);
    }
  }
}
